import {
  createThemes,
  DEFAULT_PALETTE,
  DEFAULT_SUBDUED_PALETTE,
} from '@bsky.app/alf'

import {BRAND} from '#/lib/brand'

/*
 * BRAND-AWARE PRIMARY. The vendored alf DEFAULT_PALETTE bakes cooey's red in
 * as primary_25..975 — which made EVERY brand's shell (signed-out splash bar,
 * primary buttons, link tints) render cooey red regardless of the compiled
 * brand accent (x.ccisd.me and x.bhisd.me both shipped red CTAs; see
 * pds-pro docs/brand-provisioning.md, bug 1). Re-hue the primary scale from
 * BRAND.accent: each step keeps the default ladder's LIGHTNESS (so the
 * contrast ergonomics alf was designed around survive) and takes the brand's
 * hue + saturation. Cooey's accent IS the default red, so cooey resolves to
 * the identical palette.
 */
function hexToHsl(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  const r = ((n >> 16) & 255) / 255
  const g = ((n >> 8) & 255) / 255
  const b = (n & 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6
  return [h * 360, s, l]
}

function hslToHex(h: number, s: number, l: number): string {
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const a = s * Math.min(l, 1 - l)
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
    return Math.round(255 * c)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

function brandPrimaryPalette(base: typeof DEFAULT_PALETTE) {
  const accent = hexToHsl(BRAND.accent)
  const tint = BRAND.groundTint
  const out = {...base} as Record<string, string>
  for (const [key, value] of Object.entries(base)) {
    if (typeof value !== 'string') continue
    const step = hexToHsl(value)
    if (!step) continue
    // Re-hue the primary ramp from BRAND.accent (all brands).
    if (key.startsWith('primary_') && accent) {
      out[key] = hslToHex(accent[0], accent[1], step[2])
      // Re-hue the neutral bg/ink/border ramp toward BRAND.groundTint, if set —
      // same lightness-preserving trick, so a brand can have a coloured ground.
    } else if (key.startsWith('contrast_') && tint) {
      out[key] = hslToHex(tint.hue, tint.sat, step[2])
    }
  }
  return out as typeof base
}

const BRAND_PALETTE = brandPrimaryPalette(DEFAULT_PALETTE)

const DEFAULT_THEMES = createThemes({
  defaultPalette: BRAND_PALETTE,
  subduedPalette: brandPrimaryPalette(DEFAULT_SUBDUED_PALETTE),
})

export const themes = {
  lightPalette: DEFAULT_THEMES.light.palette,
  darkPalette: DEFAULT_THEMES.dark.palette,
  dimPalette: DEFAULT_THEMES.dim.palette,
  light: DEFAULT_THEMES.light,
  dark: DEFAULT_THEMES.dark,
  dim: DEFAULT_THEMES.dim,
}

/**
 * @deprecated use ALF and access palette from `useTheme()`
 */
export const lightPalette = DEFAULT_THEMES.light.palette
/**
 * @deprecated use ALF and access palette from `useTheme()`
 */
export const darkPalette = DEFAULT_THEMES.dark.palette
/**
 * @deprecated use ALF and access palette from `useTheme()`
 */
export const dimPalette = DEFAULT_THEMES.dim.palette
/**
 * @deprecated use ALF and access theme from `useTheme()`
 */
export const light = DEFAULT_THEMES.light
/**
 * @deprecated use ALF and access theme from `useTheme()`
 */
export const dark = DEFAULT_THEMES.dark
/**
 * @deprecated use ALF and access theme from `useTheme()`
 */
export const dim = DEFAULT_THEMES.dim
